'use client'

import { useState, useEffect, useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CustomDropdown } from '@/components/ui/custom-dropdown'
import { X, Save, Search, XCircle, Loader2, Sparkles } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { getAuthHeaders } from '@/lib/api-utils'
import { assignIssueToSprint } from '@/lib/sprint-assignment'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/lib/stores/auth-store'
import { SprintSelector } from '@/components/sprints/sprint-selector'
import { StoryPointInput } from '@/components/issues/story-point-input'
import { useIssueSprintMapping } from '@/hooks/use-sprints'

interface Issue {
  id: number
  subject: string
  description?: string
  status: {
    id: number
    name: string
  }
  priority: {
    id: number
    name: string
  }
  tracker: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  category?: {
    id: number
    name: string
  }
  parent?: {
    id: number
    subject: string
  }
  children?: Array<{
    id: number
    subject: string
  }>
  start_date?: string
  due_date?: string
  done_ratio: number
  estimated_hours?: number
  project: {
    id: number
    name: string
  }
}

interface EditIssueModalProps {
  issue?: Issue | null
  isOpen: boolean
  onClose: () => void
  onSuccess?: (updatedIssue?: any) => void
  parentIssueId?: number
  projectId?: number
}

interface Tracker {
  id: number
  name: string
  enabled_standard_fields?: string[]
  disabled_core_fields?: string[]
}

interface Status {
  id: number
  name: string
}

interface Priority {
  id: number
  name: string
}

interface User {
  id: number
  name: string
}

interface Version {
  id: number
  name: string
}

interface Category {
  id: number
  name: string
}

interface Project {
  id: number
  name: string
  identifier: string
}

interface EditModalData {
  trackers: Tracker[]
  statuses: Status[]
  priorities: Priority[]
  members: User[]
  versions: Version[]
  categories: Category[]
  projects: Project[]
}

export function EditIssueModal({ issue, isOpen, onClose, onSuccess, parentIssueId, projectId }: EditIssueModalProps) {
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const router = useRouter()
  const { toast } = useToast()
  const { user } = useAuth()
  const t = useTranslations('issuesPage')
  const c = useTranslations('common')
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [modalData, setModalData] = useState<EditModalData | null>(null)
  const [enhancingDescription, setEnhancingDescription] = useState(false)

  // Parent task search states
  const [parentTaskSearch, setParentTaskSearch] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [selectedParentTask, setSelectedParentTask] = useState<any>(issue?.parent || null)
  const [isSearching, setIsSearching] = useState(false)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Sprint management states
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null)
  const [selectedSprintProjectId, setSelectedSprintProjectId] = useState<number | null>(null)
  const [storyPoints, setStoryPoints] = useState<number | null>(null)
  
  // Parent project hierarchy for sprint selection
  const [parentProjectIds, setParentProjectIds] = useState<number[]>([])
  const [projectNamesMap, setProjectNamesMap] = useState<Record<number, string>>({})

  // Load existing sprint mapping when editing an issue
  const { mapping: existingMapping, loading: loadingMapping } = useIssueSprintMapping(
    issue?.project.id || null,
    issue?.id || null
  )

  // Set sprint data when mapping loads
  useEffect(() => {
    if (existingMapping) {
      setSelectedSprintId(String(existingMapping.sprintId))
      setStoryPoints(existingMapping.storyPoints ?? null)
    } else if (!loadingMapping) {
      // If mapping finished loading but there's no mapping, clear values
      setSelectedSprintId(null)
      setStoryPoints(null)
    }
  }, [existingMapping, loadingMapping])

  // Reset sprint/story points when modal opens with different issue or closes
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes to avoid stale data on next open
      setSelectedSprintId(null)
      setStoryPoints(null)
      setSelectedSprintProjectId(null)
    } else if (isOpen && issue?.id) {
      // When modal opens with a new issue, reset immediately while waiting for mapping to load
      // This prevents showing stale values from previous issue
      setSelectedSprintId(null)
      setStoryPoints(null)
      setSelectedSprintProjectId(null)
    }
  }, [isOpen, issue?.id])

  // Helper functions to get display values
  const getTrackerName = (id: string) => {
    if (!id || !modalData) return ''
    const tracker = modalData.trackers.find(t => t.id.toString() === id)
    return tracker?.name || ''
  }

  const getStatusName = (id: string) => {
    if (!id || !modalData) return ''
    const status = modalData.statuses.find(s => s.id.toString() === id)
    return status?.name || ''
  }

  const getPriorityName = (id: string) => {
    if (!id || !modalData) return ''
    const priority = modalData.priorities.find(p => p.id.toString() === id)
    return priority?.name || ''
  }

  const getAssigneeName = (id: string) => {
    if (!id || !modalData) return ''
    if (id === 'null' || id === '') return '(nobody)'
    const member = modalData.members.find(m => m.id.toString() === id)
    return member?.name || ''
  }

  const getVersionName = (id: string) => {
    if (!id || !modalData) return ''
    if (id === 'null' || id === '') return '(none)'
    const version = modalData.versions.find(v => v.id.toString() === id)
    return version?.name || ''
  }

  const getCategoryName = (id: string) => {
    if (!id || !modalData) return ''
    if (id === 'null' || id === '') return '(none)'
    const category = modalData.categories.find(c => c.id.toString() === id)
    return category?.name || ''
  }

  const getProjectName = (id: string) => {
    if (!id || !modalData) return ''
    const project = modalData.projects.find(p => p.id.toString() === id)
    return project?.name || ''
  }

  const isCreating = !issue
  const [formData, setFormData] = useState({
    subject: issue?.subject || '',
    description: issue?.description || '',
    project_id: (projectId || issue?.project.id)?.toString() || '',
    tracker_id: issue?.tracker.id.toString() || '',
    status_id: issue?.status.id.toString() || '',
    priority_id: issue?.priority.id.toString() || '',
    assigned_to_id: issue?.assigned_to?.id.toString() || '',
    fixed_version_id: issue?.fixed_version?.id.toString() || '',
    category_id: issue?.category?.id.toString() || '',
    parent_issue_id: parentIssueId?.toString() || issue?.parent?.id.toString() || '',
    start_date: issue?.start_date || '',
    due_date: issue?.due_date || '',
    estimated_hours: issue?.estimated_hours?.toString() || '',
    done_ratio: issue?.done_ratio.toString() || '0',
    is_private: false,
    notes: ''
  })

  // Helper function to check if done_ratio is enabled for the selected tracker
  const isDoneRatioEnabled = () => {
    if (!formData.tracker_id || !modalData?.trackers || modalData.trackers.length === 0) {
      return false
    }

    const selectedTracker = modalData.trackers.find(t => t.id.toString() === formData.tracker_id)
    if (!selectedTracker) {
      return false
    }

    // If enabled_standard_fields exists, check if done_ratio is in it
    if (selectedTracker.enabled_standard_fields) {
      const isEnabled = selectedTracker.enabled_standard_fields.includes('done_ratio')
      return isEnabled
    }

    // If disabled_core_fields exists, check if done_ratio is NOT in it
    if (selectedTracker.disabled_core_fields) {
      const isEnabled = !selectedTracker.disabled_core_fields.includes('done_ratio')
      return isEnabled
    }

    // By default, assume it's enabled (Redmine's default behavior)
    return true
  }

  const isFieldEnabled = (fieldName: string) => {
    if (!formData.tracker_id || !modalData?.trackers || modalData.trackers.length === 0) {
      return true
    }

    const selectedTracker = modalData.trackers.find(t => t.id.toString() === formData.tracker_id)
    if (!selectedTracker) {
      return true
    }

    // If enabled_standard_fields exists, check if field is in it
    if (selectedTracker.enabled_standard_fields) {
      const isEnabled = selectedTracker.enabled_standard_fields.includes(fieldName)
      return isEnabled
    }

    // If disabled_core_fields exists, check if field is NOT in it
    if (selectedTracker.disabled_core_fields) {
      const isEnabled = !selectedTracker.disabled_core_fields.includes(fieldName)
      return isEnabled
    }

    // By default, assume it's enabled
    return true
  }

  // Update form data when issue changes
  useEffect(() => {
    if (issue) {
      setFormData({
        subject: issue.subject,
        description: issue.description || '',
        project_id: issue.project.id.toString(),
        tracker_id: issue.tracker.id.toString(),
        status_id: issue.status.id.toString(),
        priority_id: issue.priority.id.toString(),
        assigned_to_id: issue.assigned_to?.id.toString() || '',
        fixed_version_id: issue.fixed_version?.id.toString() || '',
        category_id: issue.category?.id.toString() || '',
        parent_issue_id: issue.parent?.id.toString() || '',
        start_date: issue.start_date || '',
        due_date: issue.due_date || '',
        estimated_hours: issue.estimated_hours?.toString() || '',
        done_ratio: issue.done_ratio.toString(),
        is_private: false,
        notes: ''
      })

      // Fetch parent task details if only ID is available
      if (issue.parent?.id && !issue.parent?.subject) {
        fetchParentTaskDetails(issue.parent.id)
      } else {
        setSelectedParentTask(issue.parent || null)
      }
    } else if (parentIssueId) {
      // Reset for creating subtask
      setFormData({
        subject: '',
        description: '',
        project_id: projectId?.toString() || '',
        tracker_id: '',
        status_id: '',
        priority_id: '',
        assigned_to_id: '',
        fixed_version_id: '',
        category_id: '',
        parent_issue_id: parentIssueId.toString(),
        start_date: '',
        due_date: '',
        estimated_hours: '',
        done_ratio: '0',
        is_private: false,
        notes: ''
      })

      // Fetch parent task details for the parent issue ID
      fetchParentTaskDetails(parentIssueId)
    }
  }, [issue, parentIssueId])

  // Fetch parent task details by ID
  const fetchParentTaskDetails = async (parentId: number) => {
    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      const response = await fetch(`/api/issues/${parentId}`, {
        headers: authHeaders
      })

      if (response.ok) {
        const data = await response.json()
        if (data.issue) {
          setSelectedParentTask(data.issue)
        }
      }
    } catch (error) {
    }
  }

  // Fetch modal data when opened or when issue status changes
  useEffect(() => {
    if (isOpen) {
      fetchModalData()
    }
  }, [isOpen, issue?.status?.id])

  const fetchModalData = async () => {
    try {
      setLoading(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      const effectiveProjectId = projectId || issue?.project.id
      if (!effectiveProjectId) return

      // For existing issues, fetch allowed statuses; for new issues, fetch all statuses
      const statusEndpoint = issue?.id
        ? `/api/issues/${issue.id}/allowed_statuses`
        : '/api/issue_statuses'

      const [statusesRes, trackersRes, prioritiesRes, versionsRes, membersRes, currentUserRes, projectsRes] = await Promise.allSettled([
        fetch(statusEndpoint, { headers: authHeaders }),
        fetch('/api/trackers', { headers: authHeaders }),
        fetch('/api/enumerations/issue_priorities', { headers: authHeaders }),
        fetch(`/api/projects/${effectiveProjectId}/versions`, { headers: authHeaders }),
        fetch(`/api/projects/${effectiveProjectId}/memberships`, { headers: authHeaders }),
        fetch('/api/users/current', { headers: authHeaders }),
        fetch('/api/projects?limit=100', { headers: authHeaders })
      ])

      const data: EditModalData = {
        statuses: [],
        trackers: [],
        priorities: [],
        versions: [],
        members: [],
        categories: [],
        projects: []
      }

      if (statusesRes.status === 'fulfilled' && statusesRes.value.ok) {
        const statusData = await statusesRes.value.json()
        // Handle both allowed_statuses (from issue endpoint) and issue_statuses (from general endpoint)
        data.statuses = statusData.allowed_statuses || statusData.issue_statuses || []
                // Always include current status for edit modal (so user can see current state)
        if (issue && !data.statuses.find((s: any) => s.id === issue.status.id)) {
                    data.statuses.unshift(issue.status)
        }
      }

      if (trackersRes.status === 'fulfilled' && trackersRes.value.ok) {
        const trackerData = await trackersRes.value.json()
        data.trackers = trackerData.trackers || []
      }

      if (prioritiesRes.status === 'fulfilled' && prioritiesRes.value.ok) {
        const priorityData = await prioritiesRes.value.json()
        data.priorities = priorityData.issue_priorities || []
      }

      if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
        const versionData = await versionsRes.value.json()
        data.versions = versionData.versions || []
      }

      // Get current user ID for sorting
      let currentUserId: number | null = null
      if (currentUserRes.status === 'fulfilled' && currentUserRes.value.ok) {
        const userData = await currentUserRes.value.json()
        currentUserId = userData.user?.id || userData.id || null
      }

      if (membersRes.status === 'fulfilled' && membersRes.value.ok) {
        const memberData = await membersRes.value.json()
        const memberships = memberData.memberships || []

                const allUserData = memberships.map((m: any, index: number) => {
          if (m.group) {
            return { index, type: 'group', groupId: m.group.id, groupName: m.group.name }
          }
          if (m.user) {
            return {
              index,
              type: 'user',
              userId: m.user.id,
              userName: m.user.name,
              userFirstname: m.user.firstname,
              userLastname: m.user.lastname
            }
          }
          return { index, type: 'unknown', data: m }
        })

        // Extract users from memberships - memberships have a 'user' property
        let members = memberships
          .map((m: any) => {
            const user = m.user
            if (!user) {
              return null
            }
            if (!user.id) {
              return null
            }

            // Build display name - some users have 'name', others have firstname/lastname
            let displayName = user.name || ''
            if (!displayName && (user.firstname || user.lastname)) {
              displayName = `${user.firstname || ''} ${user.lastname || ''}`.trim()
            }

            // Skip if no valid name found
            if (!displayName) {
              return null
            }

            return {
              id: user.id,
              name: displayName,
              originalUser: user
            }
          })
          .filter((member: any) => member !== null) // Remove null entries

        // Add "(Me)" label to current user and sort
        if (currentUserId) {
          // Check if current user is in the list
          const hasCurrentUser = members.some((m: any) => m.id === currentUserId)

          if (!hasCurrentUser) {
            // Get current user data from the API response
            if (currentUserRes.status === 'fulfilled' && currentUserRes.value.ok) {
              try {
                const userData = await currentUserRes.value.clone().json()
                const currentUserData = userData.user || userData

                if (currentUserData && currentUserData.id) {
                  // Build display name for current user
                  let currentUserName = currentUserData.name || ''
                  if (!currentUserName && (currentUserData.firstname || currentUserData.lastname)) {
                    currentUserName = `${currentUserData.firstname || ''} ${currentUserData.lastname || ''}`.trim()
                  }

                  if (currentUserName) {
                    // Add current user at the beginning
                    members.unshift({
                      id: currentUserData.id,
                      name: `${currentUserName} (Me)`,
                      originalUser: currentUserData
                    })
                  }
                }
              } catch (error) {
              }
            }
          }

          // Add "(Me)" label to current user
          members = members.map((m: any) => ({
            ...m,
            name: m.id === currentUserId ? `${m.name} (Me)` : m.name
          }))

          // Sort: current user first, then alphabetically
          members.sort((a: any, b: any) => {
            if (a.id === currentUserId) return -1
            if (b.id === currentUserId) return 1
            return a.name.localeCompare(b.name)
          })
        } else {
          // Just sort alphabetically if no current user
          members.sort((a: any, b: any) => a.name.localeCompare(b.name))
        }

        data.members = members
      }

      // Process projects and build parent hierarchy
      if (projectsRes.status === 'fulfilled' && projectsRes.value.ok) {
        const projectData = await projectsRes.value.json()
        const allProjects = projectData.projects || []
        
        data.projects = allProjects.map((p: any) => ({
          id: p.id,
          name: p.name,
          identifier: p.identifier,
          parent: p.parent // Keep parent info for hierarchy building
        }))
        
        // Build parent project hierarchy for the current issue's project
        const currentProjectId = effectiveProjectId
        const projectsById = new Map<number, any>(allProjects.map((p: any) => [p.id, p]))
        const parentIds: number[] = []
        const namesMap: Record<number, string> = {}
        
        // Add current project to names map
        const currentProject = projectsById.get(currentProjectId)
        if (currentProject) {
          namesMap[currentProjectId] = currentProject.name
        }
        
        // Traverse up the parent hierarchy (max 2 levels: parent + grandparent)
        let parentProject = currentProject?.parent as { id: number; name?: string } | undefined
        let depth = 0
        const MAX_PARENT_DEPTH = 2
        while (parentProject && parentProject.id && depth < MAX_PARENT_DEPTH) {
          parentIds.push(parentProject.id)
          const fullParent = projectsById.get(parentProject.id)
          namesMap[parentProject.id] = fullParent?.name || parentProject.name || `Project ${parentProject.id}`
          parentProject = fullParent?.parent as { id: number; name?: string } | undefined
          depth++
        }
        
        setParentProjectIds(parentIds)
        setProjectNamesMap(namesMap)
      }

      setModalData(data)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  // Handle project change - refetch project-specific data
  const handleProjectChange = async (newProjectId: string) => {
    if (!newProjectId || !modalData) return

    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      // Fetch project-specific data
      const [versionsRes, membersRes, categoriesRes] = await Promise.allSettled([
        fetch(`/api/projects/${newProjectId}/versions`, { headers: authHeaders }),
        fetch(`/api/projects/${newProjectId}/memberships`, { headers: authHeaders }),
        fetch(`/api/projects/${newProjectId}/issue_categories`, { headers: authHeaders })
      ])

      const updatedData = { ...modalData }

      // Update versions
      if (versionsRes.status === 'fulfilled' && versionsRes.value.ok) {
        const versionData = await versionsRes.value.json()
        updatedData.versions = versionData.versions || []
      }

      // Update members (reuse existing logic)
      if (membersRes.status === 'fulfilled' && membersRes.value.ok) {
        const memberData = await membersRes.value.json()
        const memberships = memberData.memberships || []
        
        let members = memberships
          .map((m: any) => {
            const user = m.user
            if (!user || !user.id) return null

            let displayName = user.name || ''
            if (!displayName && (user.firstname || user.lastname)) {
              displayName = `${user.firstname || ''} ${user.lastname || ''}`.trim()
            }
            if (!displayName) return null

            return {
              id: user.id,
              name: displayName,
              originalUser: user
            }
          })
          .filter((m: any) => m !== null)

        members.sort((a: any, b: any) => a.name.localeCompare(b.name))
        updatedData.members = members
      }

      // Update categories
      if (categoriesRes.status === 'fulfilled' && categoriesRes.value.ok) {
        const categoryData = await categoriesRes.value.json()
        updatedData.categories = (categoryData.issue_categories || []).map((c: any) => ({
          id: c.id,
          name: c.name
        }))
      }

      setModalData(updatedData)
      
      // Reset project-specific fields
      setFormData(prev => ({
        ...prev,
        project_id: newProjectId,
        assigned_to_id: '',
        fixed_version_id: '',
        category_id: ''
      }))

      toast({
        title: t('projectChanged'),
        description: t('projectChangedDescription'),
      })
    } catch (error) {
      console.error('Error fetching project data:', error)
      toast({
        title: c('error'),
        description: t('errorLoadingProjectData'),
        variant: 'destructive'
      })
    }
  }

  // Enhance description with AI
  const enhanceDescription = async () => {
    if (!formData.description.trim() || enhancingDescription) return

    setEnhancingDescription(true)
    try {
      const authHeaders = getAuthHeaders()

      const response = await fetch('/api/assistant/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          userMessage: `TASK: Enhance the following description. Make it more clear, professional, and detailed while keeping the original meaning.

CRITICAL RULES:
- Return ONLY the enhanced description text - nothing else
- Do NOT include any preamble, explanation, or meta-commentary
- Do NOT include ANY headers like "Enhanced Task Description:", "Action Required:", etc.
- Do NOT include ANY bold/italic formatting for headers (**Header:**)
- Do NOT include suggestions, recommendations, or additional sections
- Do NOT include phrases like "Here is..." or "I've improved..."
- Do NOT add markdown headers (# or ##)
- Just return the plain enhanced text as if you're writing the description yourself

ORIGINAL DESCRIPTION:
${formData.description}

ENHANCED DESCRIPTION (plain text only):`,
          context: {
            userId: user?.id?.toString() || '1',
            currentPage: 'issue-creation',
            action: 'enhance-description'
          },
          assistantType: 'enhanceDescription',
          source: 'other' // Don't send to Slack
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to enhance description')
      }

      const data = await response.json()
      if (data.response) {
        // Clean the response to remove any preamble or suggestions
        let cleanedResponse = data.response.trim()

        // Remove common preambles
        cleanedResponse = cleanedResponse.replace(/^(Here is|Here's|I've|I have)\s+(the\s+)?(enhanced|improved|updated|rewritten)\s+(description|version|text)[:.]?\s*/i, '')
        cleanedResponse = cleanedResponse.replace(/^(Enhanced|Improved|Updated|Rewritten)\s+(description|version|text)[:.]?\s*/i, '')

        // Remove ALL markdown headers (## or **Header:**)
        cleanedResponse = cleanedResponse.replace(/^#{1,6}\s+.*$/gm, '')
        cleanedResponse = cleanedResponse.replace(/^\*\*[^*]+:\*\*\s*$/gm, '')

        // Remove bold headers followed by content (like **Action Required:** or **Enhanced Task Description:**)
        cleanedResponse = cleanedResponse.replace(/\*\*[^*]+:\*\*\s*/g, '')

        // Remove "Suggestions:" and everything after it
        cleanedResponse = cleanedResponse.replace(/\n\n(Suggestions?|Additional\s+Notes?|Recommendations?|Notes?)[:.]?\s*[\s\S]*$/i, '')

        // Remove markdown headers that might indicate suggestions section
        cleanedResponse = cleanedResponse.replace(/\n#{1,6}\s*(Suggestions?|Additional\s+Notes?|Recommendations?|Notes?)[\s\S]*$/i, '')

        // Remove lines that are just section dividers or headers
        cleanedResponse = cleanedResponse.replace(/^[*\-=_]{3,}\s*$/gm, '')

        // Remove empty lines at the start and end, and reduce multiple blank lines to one
        cleanedResponse = cleanedResponse.replace(/^\n+/, '').replace(/\n+$/, '')
        cleanedResponse = cleanedResponse.replace(/\n{3,}/g, '\n\n')

        // Trim again after cleaning
        cleanedResponse = cleanedResponse.trim()

        setFormData({ ...formData, description: cleanedResponse })
        toast({
          title: 'Description Enhanced',
          description: 'Your description has been improved with AI.',
        })
      }
    } catch (error) {
      console.error('Error enhancing description:', error)
      toast({
        variant: 'destructive',
        title: 'Enhancement Failed',
        description: error instanceof Error ? error.message : 'Failed to enhance description. Please try again.',
      })
    } finally {
      setEnhancingDescription(false)
    }
  }

  // Real-time parent task search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!parentTaskSearch.trim() || parentTaskSearch.length < 1) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    setIsSearching(true)

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const authHeaders = getAuthHeaders()
        const searchQuery = parentTaskSearch.trim()
        const effectiveProjectId = projectId || issue?.project.id

        // Check if search query is a number (issue ID)
        const isNumericSearch = /^\d+$/.test(searchQuery)

        let allResults: any[] = []

        if (isNumericSearch) {
          // For numeric search, try both exact ID match and subject search
          const issueId = parseInt(searchQuery)

          // Try to fetch the specific issue by ID
          try {
            const exactIssueResponse = await fetch(`/api/issues/${issueId}`, {
              headers: { ...authHeaders }
            })
            if (exactIssueResponse.ok) {
              const exactIssue = await exactIssueResponse.json()
              // Check if it matches the project filter (if any)
              if (!effectiveProjectId || exactIssue.issue?.project?.id === effectiveProjectId) {
                allResults.push(exactIssue.issue)
              }
            }
          } catch (err) {
            // Issue not found by exact ID, continue with text search
          }

          // Also search for issues containing this number in subject
          const projectFilter = effectiveProjectId ? `&project_id=${effectiveProjectId}` : ''
          const subjectSearchUrl = `/api/issues?limit=10&subject=~${searchQuery}${projectFilter}&status_id=*`
          const subjectResponse = await fetch(subjectSearchUrl, { headers: { ...authHeaders } })

          if (subjectResponse.ok) {
            const subjectData = await subjectResponse.json()
            const subjectIssues = subjectData.issues || []

            // Merge results, avoiding duplicates
            subjectIssues.forEach((subjectIssue: any) => {
              if (!allResults.find(r => r.id === subjectIssue.id)) {
                allResults.push(subjectIssue)
              }
            })
          }
        } else {
          // Text search by subject
          const projectFilter = effectiveProjectId ? `&project_id=${effectiveProjectId}` : ''
          const encodedQuery = encodeURIComponent(searchQuery)
          const searchUrl = `/api/issues?limit=10&subject=~${encodedQuery}${projectFilter}&status_id=*`

          const response = await fetch(searchUrl, { headers: { ...authHeaders } })

          if (response.ok) {
            const data = await response.json()
            allResults = data.issues || []
          }
        }

        // Filter out the current issue if editing
        const filteredIssues = issue
          ? allResults.filter((i: any) => i.id !== issue.id)
          : allResults

        setSearchResults(filteredIssues)
        setShowSearchResults(true)
      } catch (error) {
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [parentTaskSearch, projectId, issue])

  const handleParentTaskSelect = (task: any) => {
    setSelectedParentTask(task)
    setFormData({ ...formData, parent_issue_id: task.id.toString() })
    setParentTaskSearch('')
    setShowSearchResults(false)
    setSearchResults([])
  }

  const handleRemoveParentTask = () => {
    setSelectedParentTask(null)
    setFormData({ ...formData, parent_issue_id: '' })
  }

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      // Only close on Escape key, not other keys like Backspace
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Get the current scroll position
      const scrollY = window.scrollY

      // Store original overflow values
      const originalOverflow = document.body.style.overflow
      const originalPaddingRight = document.body.style.paddingRight

      // Get scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

      // Prevent body scroll
      document.body.style.overflow = 'hidden'

      // Add padding to compensate for scrollbar removal (prevents layout shift)
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }

      return () => {
        // Restore body scroll
        document.body.style.overflow = originalOverflow
        document.body.style.paddingRight = originalPaddingRight
      }
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      const authHeaders = getAuthHeaders()
      if (!authHeaders) return

      // Always required fields
      const issuePayload: any = {
        subject: formData.subject,
        description: formData.description,
        tracker_id: parseInt(formData.tracker_id),
        status_id: parseInt(formData.status_id),
        priority_id: parseInt(formData.priority_id)
      }

      // Only add done_ratio if it's enabled for this tracker AND issue doesn't have children
      const hasChildren = issue?.children && issue.children.length > 0
      if (isDoneRatioEnabled() && formData.done_ratio && !hasChildren) {
        issuePayload.done_ratio = parseInt(formData.done_ratio)
      } else {
        if (hasChildren) {
        } else {
        }
      }

      // Add optional fields only if enabled
      // Note: Always include assigned_to_id if it has a value, even if not "enabled" 
      // The Redmine API will handle validation if the field is truly not allowed
      if (formData.assigned_to_id) {
        issuePayload.assigned_to_id = parseInt(formData.assigned_to_id)
      }

      if (isFieldEnabled('fixed_version_id') && formData.fixed_version_id) {
        issuePayload.fixed_version_id = parseInt(formData.fixed_version_id)
      }

      if (isFieldEnabled('category_id') && formData.category_id) {
        issuePayload.category_id = parseInt(formData.category_id)
      }

      if (isFieldEnabled('start_date') && formData.start_date) {
        issuePayload.start_date = formData.start_date
      }

      if (isFieldEnabled('due_date') && formData.due_date) {
        issuePayload.due_date = formData.due_date
      }

      if (isFieldEnabled('estimated_hours') && formData.estimated_hours) {
        issuePayload.estimated_hours = parseFloat(formData.estimated_hours)
      }

      if (formData.notes) issuePayload.notes = formData.notes
      // Handle parent_issue_id explicitly: if set, send its numeric value.
      // If the user cleared the parent (empty string) and the issue originally had a parent,
      // send null to instruct Redmine to remove the parent relationship.
      if (formData.parent_issue_id) {
        issuePayload.parent_issue_id = parseInt(formData.parent_issue_id)
      } else if (formData.parent_issue_id === '' && issue?.parent) {
        // Explicitly remove parent relationship
        issuePayload.parent_issue_id = null
      }
      if (formData.is_private) issuePayload.is_private = formData.is_private

      // Always include project_id when it's set (for both creating and updating)
      const effectiveProjectId = formData.project_id || projectId || issue?.project.id
      if (effectiveProjectId) {
        issuePayload.project_id = typeof effectiveProjectId === 'string' 
          ? parseInt(effectiveProjectId) 
          : effectiveProjectId
      }

      const url = isCreating ? '/api/issues' : `/api/issues/${issue!.id}`
      const method = isCreating ? 'POST' : 'PUT'

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ issue: issuePayload })
      })

      if (response.ok) {
        const result = await response.json()
        const newIssue = result.issue
        console.log('[EditIssueModal] Redmine response result:', result)
        console.log('[EditIssueModal] newIssue:', newIssue)
        console.log('[EditIssueModal] isCreating:', isCreating, 'existing issue:', issue?.id)

        // Handle sprint assignment (save to database)
        // For updates, issue?.id is the ID we need (newIssue might be undefined)
        const targetIssueId = newIssue?.id || issue?.id
        console.log('[EditIssueModal] targetIssueId for sprint assignment:', targetIssueId)
        console.log('[EditIssueModal] selectedSprintId:', selectedSprintId, 'storyPoints:', storyPoints)
        
        if (targetIssueId && effectiveProjectId) {
          try {
            const issueProjectId = parseInt(effectiveProjectId.toString())
            const sprintIdToAssign = selectedSprintId ? parseInt(selectedSprintId, 10) : null
            console.log('[EditIssueModal] Calling assignIssueToSprint with:', targetIssueId, sprintIdToAssign, storyPoints)

            // Use centralized sprint assignment function
            const result = await assignIssueToSprint(
              targetIssueId,
              sprintIdToAssign,
              storyPoints || null
            )
            console.log('[EditIssueModal] assignIssueToSprint result:', result)

            if (!result.success) {
              console.error('Failed to assign issue to sprint:', result.error)
            }
          } catch (sprintError) {
            console.error('Error updating sprint assignment:', sprintError)
            // Don't fail the whole operation, just log
          }
        } else {
          console.log('[EditIssueModal] Skipping sprint assignment - targetIssueId:', targetIssueId, 'effectiveProjectId:', effectiveProjectId)
        }

        // Show success toast
        toast({
          title: isCreating ? t('issueCreatedTitle') : t('issueUpdatedTitle'),
          description: isCreating
            ? t('issueCreatedDescription', { id: newIssue.id, subject: formData.subject })
            : t('issueUpdatedDescription', { subject: formData.subject }),
          variant: 'default'
        })

        // Close the modal first
        onClose()

        // If creating a new issue, navigate to it
        if (isCreating && newIssue?.id) {
          // Small delay to allow modal close animation
          setTimeout(() => {
            router.push(`/issues/${newIssue.id}`)
          }, 300)
        } else {
          // Call success callback to refresh data for updates
          if (onSuccess) {
            onSuccess(newIssue)
          }
        }
      } else {
        const error = await response.json()

        // Parse and format error message
        let errorMsg = `Failed to ${isCreating ? 'create' : 'update'} issue`

        if (error.error) {
          errorMsg = error.error
        } else if (error.errors) {
          const errors = Array.isArray(error.errors) ? error.errors : [error.errors]
          errorMsg = errors.join('\n')
        }

        // Show detailed error for workflow/validation issues
        if (response.status === 403) {
          errorMsg = '⚠️ Workflow Error: ' + errorMsg + ' This status transition is not allowed based on your project\'s workflow configuration.'
        } else if (response.status === 422) {
          errorMsg = '⚠️ Validation Error: ' + errorMsg
        }

        toast({
          title: isCreating ? t('failedToCreateIssueTitle') : t('failedToUpdateIssueTitle'),
          description: errorMsg,
          variant: 'destructive'
        })
      }
    } catch (error) {
      toast({
        title: isCreating ? t('failedToCreateIssueTitle') : t('failedToUpdateIssueTitle'),
        description: t('unexpectedError'),
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop itself, not children
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const sidebarContent = (
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[10000] overflow-hidden"
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Sidebar Panel */}
      <div
        className={cn(
          "absolute top-0 bottom-0 w-full max-w-2xl h-full bg-background text-foreground shadow-xl flex flex-col border-border",
          isRTL ? "left-0 border-r" : "right-0 border-l"
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4 shrink-0">
          <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {isCreating ? (parentIssueId ? t('createSubtask') : t('createIssue')) : t('editIssueTitle', { id: issue?.id })}
                </h3>
            {issue && (
              <p className="text-sm text-muted-foreground mt-1">
                {issue.subject}
              </p>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading && !modalData ? (
            <div className="p-6 space-y-8">
              {/* Basic Information Skeleton */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <div className="space-y-4">
                  <div>
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-[250px] w-full" />
                  </div>
                </div>
              </div>

              {/* Issue Properties Skeleton */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-2" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Date Fields Skeleton */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <Skeleton className="h-4 w-20 mb-2" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="edit-issue-form" className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto space-y-0 divide-y">
                {/* Basic Information */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('basicInformation')}</h4>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="edit_subject" className="block text-sm font-medium text-foreground mb-2">
                      {t('fields.subject')} <span className="text-destructive">*</span>
                    </label>
                    <Input
                      id="edit_subject"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Issue subject"
                      className="h-11"
                    />
                  </div>

                  {/* Description with MD Editor */}
                  <div>
                    <label htmlFor="edit_description" className="block text-sm font-medium text-foreground mb-2">
                      Description
                    </label>
                    <MarkdownEditor
                      value={formData.description}
                      onChange={(value) => setFormData({ ...formData, description: value })}
                      placeholder="Enter issue description..."
                      height={250}
                    />
                    {/* Enhance with AI Button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mt-0 rounded-t-none border-t-0 hover:bg-primary/5"
                      onClick={enhanceDescription}
                      disabled={!formData.description.trim() || enhancingDescription}
                    >
                      {enhancingDescription ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Enhancing with AI...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Enhance with AI
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Issue Properties */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Issue Properties</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Project - Only show for editing existing issues */}
                    {issue && modalData && modalData.projects.length > 0 && (
                      <CustomDropdown
                        label={`${t('fields.project')} *`}
                        currentValue={getProjectName(formData.project_id)}
                        options={modalData.projects.map(p => ({ id: p.id, name: p.name }))}
                        onSelect={(value) => handleProjectChange(value?.toString() || '')}
                        placeholder={`${c('select')} ${t('fields.project').toLowerCase()}...`}
                      />
                    )}

                    {/* Tracker */}
                    {modalData && modalData.trackers.length > 0 && (
                      <CustomDropdown
                        label={`${t('fields.tracker')} *`}
                        currentValue={getTrackerName(formData.tracker_id)}
                        options={modalData.trackers.map(t => ({ id: t.id, name: t.name }))}
                        onSelect={(value) => setFormData({ ...formData, tracker_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('fields.tracker').toLowerCase()}...`}
                      />
                    )}

                    {/* Status */}
                    {modalData && modalData.statuses.length > 0 && (
                      <CustomDropdown
                        label={`${t('fields.status')} *`}
                        currentValue={getStatusName(formData.status_id)}
                        options={modalData.statuses.map(s => ({ id: s.id, name: s.name }))}
                        onSelect={(value) => setFormData({ ...formData, status_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('fields.status').toLowerCase()}...`}
                      />
                    )}

                    {/* Priority */}
                    {modalData && modalData.priorities.length > 0 && (
                      <CustomDropdown
                        label={`${t('fields.priority')} *`}
                        currentValue={getPriorityName(formData.priority_id)}
                        options={modalData.priorities.map(p => ({ id: p.id, name: p.name }))}
                        onSelect={(value) => setFormData({ ...formData, priority_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('fields.priority').toLowerCase()}...`}
                      />
                    )}

                    {/* Assignee */}
                    {modalData && isFieldEnabled('assigned_to_id') && (
                      <CustomDropdown
                        label={t('assignee')}
                        currentValue={getAssigneeName(formData.assigned_to_id)}
                        options={[
                          { id: null, name: t('unassigned') },
                          ...modalData.members.map(m => ({ id: m.id, name: m.name }))
                        ]}
                        onSelect={(value) => setFormData({ ...formData, assigned_to_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('fields.assignee').toLowerCase()}...`}
                      />
                    )}

                    {/* Category */}
                    {modalData && modalData.categories.length > 0 && isFieldEnabled('category_id') && (
                      <CustomDropdown
                        label={t('category')}
                        currentValue={getCategoryName(formData.category_id)}
                        options={[
                          { id: null, name: t('notAvailable') },
                          ...modalData.categories.map(c => ({ id: c.id, name: c.name }))
                        ]}
                        onSelect={(value) => setFormData({ ...formData, category_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('fields.category').toLowerCase()}...`}
                      />
                    )}

                    {/* Target Version */}
                    {modalData && modalData.versions.length > 0 && isFieldEnabled('fixed_version_id') && (
                      <CustomDropdown
                        label={t('targetVersion')}
                        currentValue={getVersionName(formData.fixed_version_id)}
                        options={[
                          { id: null, name: t('notAvailable') },
                          ...modalData.versions.map(v => ({ id: v.id, name: v.name }))
                        ]}
                        onSelect={(value) => setFormData({ ...formData, fixed_version_id: value?.toString() || '' })}
                        placeholder={`${c('select')} ${t('targetVersion').toLowerCase()}...`}
                      />
                    )}
                  </div>
                </div>

                {/* Sprint Planning */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Sprint Planning</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Sprint */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Sprint</label>
                      <SprintSelector
                        projectId={formData.project_id ? parseInt(formData.project_id) : null}
                        value={selectedSprintId}
                        onChange={(sprintId, sprintProjectId) => {
                          setSelectedSprintId(sprintId)
                          setSelectedSprintProjectId(sprintProjectId || null)
                        }}
                        showOnlyActive={false}
                        allowClear={true}
                        includeParentProjects={true}
                        parentProjectIds={parentProjectIds}
                        projectNames={projectNamesMap}
                      />
                    </div>

                    {/* Story Points */}
                    <div>
                      <StoryPointInput
                        value={storyPoints}
                        onChange={setStoryPoints}
                        fibonacci={true}
                        mode="select"
                      />
                    </div>
                  </div>
                </div>

                {/* Relationships */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">{t('relationships')}</h4>
                  </div>

                  {/* Parent Task - Live Search */}
                  <div className="relative">
                    <label htmlFor="parent_task" className="block text-sm font-medium text-foreground mb-2">
                      {t('fields.parentTask')}
                    </label>

                    {selectedParentTask ? (
                      <div className="flex items-center gap-2 p-3 border border-input rounded-md bg-muted/50">
                        <div className="flex-1">
                          <div className="text-sm font-medium">#{selectedParentTask.id}: {selectedParentTask.subject}</div>
                          <div className="text-xs text-muted-foreground">
                            {selectedParentTask.project?.name}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveParentTask}
                        className="h-8 w-8 p-0"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="parent_task"
                          value={parentTaskSearch}
                          onChange={(e) => setParentTaskSearch(e.target.value)}
                          placeholder={t('searchByTitleOrNumber')}
                          className="pl-9"
                        />
                        {isSearching && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                      </div>

                      {showSearchResults && searchResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-input rounded-md shadow-lg max-h-[200px] overflow-y-auto z-50">
                          {searchResults.map((task) => (
                            <button
                              key={task.id}
                              type="button"
                              onClick={() => handleParentTaskSelect(task)}
                              className="w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b last:border-0"
                            >
                              <div className="text-sm font-medium">#{task.id}: {task.subject}</div>
                              <div className="text-xs text-muted-foreground">
                                {task.project?.name} · {task.tracker?.name} · {task.status?.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {showSearchResults && searchResults.length === 0 && !isSearching && parentTaskSearch.length >= 2 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-input rounded-md shadow-lg p-3 z-50">
                          <p className="text-sm text-muted-foreground text-center">{t('noTasksFound')}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Planning & Tracking */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Planning & Tracking</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Start Date */}
                    {isFieldEnabled('start_date') && (
                      <div>
                        <label htmlFor="edit_start_date" className="block text-sm font-medium text-foreground mb-2">
                          Start Date
                        </label>
                        <Input
                          id="edit_start_date"
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Due Date */}
                    {isFieldEnabled('due_date') && (
                      <div>
                        <label htmlFor="edit_due_date" className="block text-sm font-medium text-foreground mb-2">
                          Due Date
                        </label>
                        <Input
                          id="edit_due_date"
                          type="date"
                          value={formData.due_date}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        />
                      </div>
                    )}

                    {/* Estimated Hours */}
                    {isFieldEnabled('estimated_hours') && (
                      <div>
                        <label htmlFor="edit_estimated_hours" className="block text-sm font-medium text-foreground mb-2">
                          Estimated Hours
                        </label>
                        <Input
                          id="edit_estimated_hours"
                          type="number"
                          step="0.5"
                          min="0"
                          value={formData.estimated_hours}
                          onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                          placeholder="0"
                        />
                      </div>
                    )}

                    {/* % Done - Input Field */}
                    {isDoneRatioEnabled() && (
                      <div>
                        <label htmlFor="edit_done_ratio" className="block text-sm font-medium text-foreground mb-2">
                          % Done
                        </label>
                        <Input
                          id="edit_done_ratio"
                          type="number"
                          min="0"
                          max="100"
                          step="10"
                          value={formData.done_ratio}
                          onChange={(e) => {
                            const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                            setFormData({ ...formData, done_ratio: value.toString() })
                          }}
                          placeholder="0"
                          disabled={issue?.children && issue.children.length > 0}
                          className={issue?.children && issue.children.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                        {issue?.children && issue.children.length > 0 && (
                          <p className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                            ⚠️ This issue has {issue.children.length} subtask(s). Progress is auto-calculated from subtasks and cannot be edited directly.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Information */}
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2 pb-2">
                    <div className="h-1 w-1 rounded-full bg-primary"></div>
                    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Additional Information</h4>
                  </div>

                  {/* Notes with MD Editor */}
                  <div>
                    <label htmlFor="edit_notes" className="block text-sm font-medium text-foreground mb-2">
                      Notes (update comment)
                    </label>
                    <MarkdownEditor
                      value={formData.notes}
                      onChange={(value) => setFormData({ ...formData, notes: value })}
                      placeholder="Enter update notes..."
                      height={180}
                    />
                  </div>

                  {/* Private Issue */}
                  <div className="flex items-center space-x-2">
                    <input
                      id="is_private"
                      type="checkbox"
                      checked={formData.is_private}
                      onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                      className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-primary cursor-pointer"
                    />
                    <label htmlFor="is_private" className="text-sm font-medium text-foreground cursor-pointer">
                      Private Issue
                    </label>
                  </div>
                </div>
              </div>
              </div>

              {/* Footer - Fixed */}
              <div className="border-t border-border bg-background shrink-0">
                <div className="p-6">
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      className="flex-1 h-11 shadow-sm"
                      disabled={loading}
                    >
                      <Save className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 h-11"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </form>
        )}
      </div>
    </div>
  </div>
  )

  return typeof window !== 'undefined' ? createPortal(sidebarContent, document.body) : null
}
